import {
  ErrorAlert,
  Heading_4 as Heading4,
  Loader,
} from '@huggg/react-components';
import dictionary from 'config/dictionary';
import PropTypes from 'prop-types';
import { compose, pathOr } from 'ramda';
import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import {
  fetchCardData,
  fetchSensitiveCardData,
  reportMisuse,
  getAvailableBalanceStr,
  getCardData,
  getFetchCardDataError,
  getFetchSensitiveCardDataError,
  getIsFetchingCardData,
  getIsFetchingSensitiveCardData,
  getSensitiveCardData,
  getSpentAmountStr,
  getIsReportingMisuse,
  getReportMisuseError,
} from 'redux/modules/prepaidCard';
import { isPrepaidHeatingCard } from 'utilities/huggg';
import CancelledCard from './CancelledCard';
import CardImage from './CardImage';
import CardInformation from './CardInformation';
import { CardRestrictionDetails } from './CardRestrictionDetails';
import styles from './prepaidCard.module.scss';

// Funding normally confirms via the provider webhook within seconds of the
// claim: poll while pending for up to 60 seconds, then stop and tell the
// recipient to check back later.
const FUNDING_POLL_INTERVAL_MS = 5000;
const MAX_FUNDING_POLL_ATTEMPTS = 12;

const getCardTitle = (segment) => {
  return pathOr(
    'Card',
    ['configuration', 'sangriaLayout', 'prepaidCard', 'title'],
    segment,
  );
};

const PrepaidCard = (props) => {
  const {
    id,
    isFetchingCardData,
    fetchCardData,
    fetchCardDataError,
    availableBalance,
    spentAmount,
    sensitiveCardData,
    isFetchingSensitiveCardData,
    fetchSensitiveCardDataError,
    fetchSensitiveCardData,
    reportMisuse,
    isReportingMisuse,
    reportMisuseError,
    cardData,
    segment,
    isHeatingCard,
  } = props;

  const cardTitle = getCardTitle(segment);

  const helpLinkToShow = useMemo(() => {
    return (
      ['energy', 'water'].find((keyword) =>
        cardTitle.toLowerCase().includes(keyword),
      ) ?? null
    );
  }, [cardTitle]);

  useEffect(() => {
    fetchCardData(id);
  }, [fetchCardData, id]);

  const isPendingFunding = cardData?.fundingStatus === 'pending';
  const [pollAttempts, setPollAttempts] = useState(0);

  useEffect(() => {
    if (!isPendingFunding || pollAttempts >= MAX_FUNDING_POLL_ATTEMPTS) {
      return undefined;
    }

    const timer = setTimeout(() => {
      setPollAttempts((attempts) => attempts + 1);
      fetchCardData(id);
    }, FUNDING_POLL_INTERVAL_MS);

    return () => clearTimeout(timer);
  }, [isPendingFunding, pollAttempts, fetchCardData, id]);

  // Only block on the loader / surface a fetch error before the first card
  // fetch has succeeded: during the funding poll the stale card stays on
  // screen rather than flickering, and a failed background poll must not
  // replace the pending state with an error.
  if (isFetchingCardData && !cardData) {
    return (
      <div className={styles.container}>
        <Loader />
      </div>
    );
  }

  if ((fetchCardDataError && !cardData) || cardData?.status === 'pending') {
    return (
      <div className={styles.container}>
        <ErrorAlert message="There was an error fetching your card, please refresh and try again." />
      </div>
    );
  }

  if (!cardData || cardData.status !== 'active') {
    return (
      <div className={styles.container}>
        <CancelledCard />
      </div>
    );
  }

  const isFailedFunding = cardData.fundingStatus === 'failed';

  if (isPendingFunding || isFailedFunding) {
    // The card exists, so show its details: the visible change from the
    // creation loader tells the recipient creation succeeded. The funding
    // state sits beneath, in place of the balance panel: a spinner while
    // polling, and check-back-later copy once funding has failed or has
    // taken longer than the 60s polling window.
    const getFundingState = () => {
      if (isFailedFunding) {
        return (
          <Heading4>{dictionary.prepaidCards.funding.failedMessage}</Heading4>
        );
      }

      if (pollAttempts >= MAX_FUNDING_POLL_ATTEMPTS) {
        return (
          <Heading4>{dictionary.prepaidCards.funding.delayedMessage}</Heading4>
        );
      }

      return (
        <>
          <Loader />
          <Heading4>{dictionary.prepaidCards.funding.loadingMessage}</Heading4>
        </>
      );
    };

    // No CardRestrictionDetails here: usage guidance (e.g. "How do I use
    // this digital card?") only makes sense once the card is ready to use.
    return (
      <div className={styles.container}>
        <CardImage
          cardData={cardData}
          cardTitle={cardTitle}
          isHeatingCard={isHeatingCard}
        />

        <div className={styles.fundingState}>{getFundingState()}</div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <CardImage
        cardData={cardData}
        cardTitle={cardTitle}
        isHeatingCard={isHeatingCard}
      />

      <CardRestrictionDetails
        helpLinkToShow={helpLinkToShow}
        isHeatingCard={isHeatingCard}
      />

      <CardInformation
        cardTitle={cardTitle}
        availableBalance={availableBalance}
        spentAmount={spentAmount}
        fetchSensitiveCardData={fetchSensitiveCardData}
        sensitiveCardData={sensitiveCardData}
        isFetchingSensitiveCardData={isFetchingSensitiveCardData}
        fetchSensitiveCardDataError={fetchSensitiveCardDataError}
        reportMisuse={reportMisuse}
        isReportingMisuse={isReportingMisuse}
        reportMisuseError={reportMisuseError}
      />
    </div>
  );
};

PrepaidCard.propTypes = {
  id: PropTypes.string.isRequired,
  cardData: PropTypes.object,
  isFetchingCardData: PropTypes.bool.isRequired,
  fetchCardDataError: PropTypes.object,
  fetchCardData: PropTypes.func.isRequired,
  availableBalance: PropTypes.string.isRequired,
  spentAmount: PropTypes.string.isRequired,
  sensitiveCardData: PropTypes.object,
  isFetchingSensitiveCardData: PropTypes.bool.isRequired,
  fetchSensitiveCardDataError: PropTypes.object,
  fetchSensitiveCardData: PropTypes.func.isRequired,
  reportMisuse: PropTypes.func.isRequired,
  isReportingMisuse: PropTypes.bool.isRequired,
  reportMisuseError: PropTypes.object,
  segment: PropTypes.object,
};

const mapStateToProps = (state) => ({
  cardData: getCardData(state),
  availableBalance: getAvailableBalanceStr(state),
  spentAmount: getSpentAmountStr(state),
  isFetchingCardData: getIsFetchingCardData(state),
  fetchCardDataError: getFetchCardDataError(state),
  sensitiveCardData: getSensitiveCardData(state),
  isFetchingSensitiveCardData: getIsFetchingSensitiveCardData(state),
  fetchSensitiveCardDataError: getFetchSensitiveCardDataError(state),
  isReportingMisuse: getIsReportingMisuse(state),
  reportMisuseError: getReportMisuseError(state),
  isHeatingCard: isPrepaidHeatingCard(state.huggg),
});

export default compose(
  connect(mapStateToProps, {
    fetchCardData,
    fetchSensitiveCardData,
    reportMisuse,
  }),
)(PrepaidCard);
